import { useState, ChangeEvent, FormEvent, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'
import ErrorBoundary from '@components/ErrorBoundary/ErrorBoundary'
import ErrorMessage from '@components/ErrorBoundary/ErrorMessage'
import ResultBox from './ResultBox'
import ResultBoxSkeleton from '@components/skeletons/ResultBoxSkeleton/ResultBoxSkeleton'
import useDatasetSearch from '@hooks/useDatasetSearch'
import type { DatasetEntry } from '@hooks/useCatalog'
import type { DataItemPayload } from 'src/types/global'

import './SearchData.css'

interface Props {
  onSelect: (item: DataItemPayload) => void
}

export default function SearchData({ onSelect }: Props) {
  const intl = useIntl()
  const { search } = useDatasetSearch()

  const searchContainerRef = useRef<HTMLElement>(null)

  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(false)
  const [results, setResults] = useState<DatasetEntry[]>([])
  const [hasSearched, setHasSearched] = useState(false)
  const [isDropdownVisible, setIsDropdownVisible] = useState(false)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setIsDropdownVisible(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    setQuery(value)

    if (value === '') {
      closeSearch()
    }
  }

  const handleInputFocus = () => {
    if (results.length > 0) {
      setIsDropdownVisible(true)
    }
  }

  const handleSearch = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const trimmedQuery = query.trim()
    if (trimmedQuery.length < 3) return

    setLoading(true)
    setHasSearched(true)
    setIsDropdownVisible(true)

    try {
      const response = await search(trimmedQuery)
      setResults(response.results)
    } catch (error) {
      console.error('Search failed:', error)
    } finally {
      setLoading(false)
    }
  }

  const closeSearch = () => {
    setQuery('')
    setResults([])
    setHasSearched(false)
    setIsDropdownVisible(false)
  }

  const isButtonDisabled = loading || query.trim().length < 3
  const isSearchOpen = loading || isDropdownVisible

  return (
    <search
      ref={searchContainerRef}
      className={`search-data ${isSearchOpen ? 'open' : ''}`}
    >
      <form onSubmit={handleSearch} className="search-form">
        <input
          className="search-input"
          type="search"
          placeholder={intl.formatMessage({ id: 'search-data.ia-placeholder' })}
          value={query}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
        />
        <button
          type="submit"
          className="icon icon-ai-search"
          aria-label="Buscar"
          disabled={isButtonDisabled}
        />
      </form>
      <ErrorBoundary errorComponent={ErrorMessage}>
        {loading && <ResultBoxSkeleton />}
        {!loading && isDropdownVisible && hasSearched && (
          <ResultBox
            datasets={results}
            onClose={closeSearch}
            onSelect={onSelect}
          />
        )}
      </ErrorBoundary>
    </search>
  )
}
